"use client";

import { useCallback, useState } from "react";
import useSWR, { mutate } from "swr";
import { toast } from "sonner";

import { Button, Spinner } from "@/components";
import { fetcher } from "@/utils";

const endpoint = "/api/hexstats";

export type HexStatsStatus = {
  ethereumDays: number;
  pulsechainDays: number;
  lastEthereumDay: number | null;
  lastPulsechainDay: number | null;
  chartsReady: boolean;
  lastSyncedAt: string | null;
};

export function useHexStatsStatus() {
  return useSWR<HexStatsStatus>(`${endpoint}/status`, fetcher, {
    revalidateOnFocus: false,
  });
}

export function useHexStatsRefresh() {
  const [loading, setLoading] = useState(false);
  const { mutate: mutateStatus } = useHexStatsStatus();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${endpoint}/refresh`, { method: "POST" });
      const body = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(body?.message ?? `Refresh failed (${res.status})`);
      }

      await mutateStatus(body, { revalidate: false });
      await mutate(
        (key) =>
          typeof key === "string" &&
          (key.startsWith("/api/v1") || key.includes("/api/v1/")),
        undefined,
        { revalidate: true },
      );

      const imported = body?.imported ?? 0;
      if (body?.mode === "up-to-date") {
        toast.success("Data is already up to date.");
      } else if (imported > 0) {
        toast.success(`Imported ${imported} new day(s).`);
      } else {
        toast.success("Charts rebuilt from stored data.");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Refresh failed.");
    } finally {
      setLoading(false);
    }
  }, [mutateStatus]);

  return { refresh, loading };
}
